import { useEffect, useState } from "react";
import { copy } from "../copy";

// Alert red is for the final hour only, and nothing else on the page.
const URGENT_MS = 60 * 60 * 1000;

const UNITS = [
  { secs: 7 * 86400, short: "w", long: "weeks" },
  { secs: 86400, short: "d", long: "days" },
  { secs: 3600, short: "h", long: "hours" },
  { secs: 60, short: "m", long: "minutes" },
  { secs: 1, short: "s", long: "seconds" },
];

type Unit = { value: string; short: string; long: string };
type Parts = { units: Unit[]; closed: boolean; urgent: boolean };

// The three largest units that matter: weeks/days/hours a month out,
// hours/minutes/seconds in the final day. A raw hour count like 1079 tells
// nobody when the contest ends.
function toParts(ms: number): Parts {
  const total = Math.max(0, Math.floor(ms / 1000));
  const first = UNITS.findIndex((u) => total >= u.secs);
  const start = Math.min(Math.max(first, 0), UNITS.length - 3);
  let rest = total;
  const units = UNITS.map((u) => {
    const n = Math.floor(rest / u.secs);
    rest -= n * u.secs;
    return { value: String(n), short: u.short, long: u.long };
  }).slice(start, start + 3);
  return {
    units: units.map((u, i) => (i === 0 ? u : { ...u, value: u.value.padStart(2, "0") })),
    closed: total <= 0,
    urgent: total > 0 && ms < URGENT_MS,
  };
}

// One flip digit group. The flip restarts whenever the value changes.
function FlipDigits({ value, unit, urgent }: { value: string; unit: string; urgent: boolean }) {
  const [shown, setShown] = useState(value);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    if (value === shown) return;
    setShown(value);
    setFlipping(true);
    const timer = window.setTimeout(() => setFlipping(false), 360);
    return () => window.clearTimeout(timer);
  }, [value, shown]);

  return (
    <span className="bf-flip inline-flex">
      <span
        className={`bf-flip-inner bf-tabular inline-flex h-9 min-w-[1.75rem] items-center justify-center rounded-md border px-1 text-lg font-bold ${
          urgent ? "border-alert/50 bg-alert/10 text-alert" : "border-ground-edge bg-ground-raised text-chrome"
        } ${flipping ? "is-flipping" : ""}`}
      >
        {shown}
        <span className={`ml-0.5 text-xs font-semibold ${urgent ? "text-alert" : "text-chrome-dim"}`}>{unit}</span>
      </span>
    </span>
  );
}

// Ticks once a second. Once the close passes it reads closed, never a
// negative countdown.
export default function Countdown({ endsAt }: { endsAt: string }) {
  const end = new Date(endsAt).getTime();
  const [parts, setParts] = useState(() => toParts(end - Date.now()));
  useEffect(() => {
    setParts(toParts(end - Date.now()));
    const id = window.setInterval(() => setParts(toParts(end - Date.now())), 1000);
    return () => window.clearInterval(id);
  }, [end]);

  if (parts.closed) {
    return (
      <span className="rounded-md border border-ground-edge bg-ground-raised px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-chrome-dim">
        {copy.live.closed}
      </span>
    );
  }

  const { units, urgent } = parts;
  const spoken = units.map((u) => `${Number(u.value)} ${u.long}`).join(" ");
  return (
    <div className="flex items-center gap-1">
      <span data-testid="countdown" className="flex items-center gap-1">
        <span className="sr-only">{copy.live.closesInSr(spoken)}</span>
        <span aria-hidden className="flex items-center gap-1">
          {units.map((u) => (
            <FlipDigits key={u.short} value={u.value} unit={u.short} urgent={urgent} />
          ))}
        </span>
      </span>
      <span className="ml-1 text-[10px] uppercase tracking-[0.16em] text-chrome-dim">
        {urgent ? copy.live.closingSoon : copy.live.leftToVote}
      </span>
    </div>
  );
}
